import { useEffect, useRef, useState, useCallback } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { DownloadSimple, Lightning, Clock, CircleNotch, CalendarBlank } from "@phosphor-icons/react";

export default function ImportFromXeroModal({ open, onClose, onImported }) {
  const [fromDate, setFromDate] = useState("");
  const [estimate, setEstimate] = useState(null); // { count, capped, recommend, from_date }
  const [checking, setChecking] = useState(false);
  const [running, setRunning] = useState(false);
  const [progress, setProgress] = useState(null); // background progress (handled by global indicator now)
  const initialisedRef = useRef(false);

  const check = useCallback(async (date) => {
    setChecking(true);
    try {
      const { data } = await api.post("/import/estimate", { from_date: date });
      setEstimate(data);
    } catch (e) {
      setEstimate(null);
      toast.error(e.response?.data?.detail || "Could not check import volume");
    } finally { setChecking(false); }
  }, []);

  // On open: run an initial estimate (backend fills the saved default date if none given).
  useEffect(() => {
    if (!open) { initialisedRef.current = false; return; }
    if (initialisedRef.current) return;
    initialisedRef.current = true;
    setEstimate(null); setProgress(null); setRunning(false);
    (async () => {
      setChecking(true);
      try {
        const { data } = await api.post("/import/estimate", {});
        setFromDate(data.from_date || "");
        setEstimate(data);
      } catch (e) {
        toast.error(e.response?.data?.detail || "Could not check import volume");
      } finally { setChecking(false); }
    })();
  }, [open]);

  // Re-estimate when the user changes the date (debounced).
  useEffect(() => {
    if (!open || !initialisedRef.current) return;
    const t = setTimeout(() => { check(fromDate); }, 500);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fromDate]);

  const finish = (result) => {
    setRunning(false); setProgress(null);
    onImported?.(result);
    onClose?.();
  };

  const run = async () => {
    if (!estimate) return;
    const mode = estimate.recommend; // "instant" | "background"
    setRunning(true);
    try {
      const { data } = await api.post("/import/run", { from_date: fromDate, mode });
      if (data.background) {
        window.dispatchEvent(new CustomEvent("import:started", { detail: data.job_id }));
        toast.info("Large import started in the background — we'll notify you when it's done. You can keep working.");
        setRunning(false);
        onClose?.();
      } else {
        finish({ imported: data.imported || 0, skipped_sources: data.skipped_sources || [] });
      }
    } catch (e) {
      setRunning(false);
      toast.error(e.response?.data?.detail || "Import failed");
    }
  };

  const isBackground = estimate?.recommend === "background";

  return (
    <Dialog open={open} onOpenChange={(o) => !o && !running && onClose?.()}>
      <DialogContent className="max-w-md rounded-lg border-slate-200" data-testid="import-xero-dialog">
        <DialogHeader>
          <div className="overline text-orangeink mb-1">Import from Xero</div>
          <DialogTitle className="font-heading font-bold text-xl tracking-tight text-navy">Pull invoices &amp; bills</DialogTitle>
          <DialogDescription className="text-navy/70">
            Choose how far back to import. We only bring in items posted to your configured account codes, and never import duplicates.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5">
          <div>
            <label className="overline text-navy/70 mb-1.5 block">Import from date</label>
            <div className="relative">
              <CalendarBlank size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-navy/65" />
              <input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)}
                disabled={running} data-testid="import-from-date"
                className="w-full pl-9 pr-3 py-2.5 text-sm rounded-lg border border-slate-200 focus:border-orange focus:ring-1 focus:ring-orange outline-none text-navy" />
            </div>
            <p className="text-xs text-navy/65 mt-1.5">Leave blank to import all available history.</p>
          </div>

          <div className="rounded-xl border border-slate-200 bg-navy/[0.02] px-4 py-3 min-h-[64px] flex items-center" data-testid="import-advisory">
            {checking ? (
              <span className="inline-flex items-center gap-2 text-sm text-navy/70">
                <CircleNotch size={16} className="animate-spin" /> Checking volume in Xero…
              </span>
            ) : progress ? (
              <span className="inline-flex items-center gap-2 text-sm text-navy">
                <CircleNotch size={16} className="animate-spin text-orangeink" /> Importing in the background… {progress.imported} item(s) so far.
              </span>
            ) : estimate ? (
              isBackground ? (
                <div className="flex items-start gap-2.5 text-sm">
                  <Clock size={18} weight="fill" className="text-warn mt-0.5 shrink-0" />
                  <span className="text-navy/80">
                    <b>Large import detected.</b> We'll run this <b>in the background</b> so it won't time out — you can keep working and we'll notify you when it's done.
                  </span>
                </div>
              ) : (
                <div className="flex items-start gap-2.5 text-sm">
                  <Lightning size={18} weight="fill" className="text-positive mt-0.5 shrink-0" />
                  <span className="text-navy/80">
                    {estimate.count > 0
                      ? <>About <b>{estimate.count}</b> recent transaction{estimate.count === 1 ? "" : "s"} to scan{estimate.from_date ? <> since <b>{estimate.from_date}</b></> : ""} — this will <b>import instantly</b>.</>
                      : <>No new transactions found{estimate.from_date ? <> since <b>{estimate.from_date}</b></> : ""} for your configured codes.</>}
                  </span>
                </div>
              )
            ) : (
              <span className="text-sm text-navy/65">Set a date to check the volume.</span>
            )}
          </div>

          <div className="flex justify-end gap-3">
            <button onClick={() => onClose?.()} disabled={running} data-testid="import-cancel"
              className="px-4 py-2.5 text-sm rounded-lg border border-slate-200 text-navy hover:bg-navy/5 transition-colors disabled:opacity-60">
              {progress ? "Close" : "Cancel"}
            </button>
            <button onClick={run} disabled={checking || running || !estimate}
              data-testid="import-run-btn"
              className="inline-flex items-center gap-2 px-5 py-2.5 text-sm font-semibold rounded-lg bg-orange text-white hover:bg-navy transition-colors disabled:opacity-60">
              {isBackground ? <Clock size={17} weight="fill" /> : <DownloadSimple size={17} />}
              {running ? "STARTING…" : isBackground ? "Import in background" : "Import now"}
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
